import { segmentEntities, type RichNode } from '@swchat/client-sdk';
import type { TextEntity } from '@swchat/protocol';
import { Fragment, useState, type ReactNode } from 'react';
import { linkHref, splitRichText } from '../lib/display';

/** Chữ thường: tô sáng tag dự án `#CODE` và mention `@Tên` (lớp `tag` / `mention` — test UI E3/A4 dựa vào). */
function plain(text: string, names: string[], inLink = false): ReactNode[] {
  return splitRichText(text, names).map((part, i) =>
    part.kind === 'text' || (part.kind === 'link' && inLink) ? (
      <Fragment key={i}>{part.text}</Fragment>
    ) : part.kind === 'link' ? (
      <a key={i} href={linkHref(part.text)} target="_blank" rel="noopener noreferrer nofollow">
        {part.text}
      </a>
    ) : (
      <span key={i} className={part.kind}>
        {part.text}
      </span>
    ),
  );
}

/** Chữ ẩn (spoiler): nhòe cho tới khi bấm/Enter vào; bấm lại thì ẩn lại. */
function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false);
  return (
    <span
      className={shown ? 'ws-spoiler is-shown' : 'ws-spoiler'}
      role="button"
      tabIndex={0}
      aria-label={shown ? 'Nội dung ẩn (đang hiện)' : 'Nội dung ẩn, bấm để hiện'}
      aria-pressed={shown}
      data-testid="spoiler"
      onClick={() => setShown((s) => !s)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setShown((s) => !s);
        }
      }}
    >
      {children}
    </span>
  );
}

function render(nodes: RichNode[], names: string[], inCode: boolean, inLink = false): ReactNode[] {
  return nodes.map((n, i) => {
    if (n.t === 'text') {
      // Khối (trích dẫn, khối mã) tự xuống dòng: bỏ một dấu xuống dòng ngay sau nó kẻo thành dòng trống thừa.
      const prev = nodes[i - 1];
      const afterBlock = prev && (prev.t === 'quote' || prev.t === 'pre') && n.text.startsWith('\n');
      const text = afterBlock ? n.text.slice(1) : n.text;
      return <Fragment key={i}>{inCode ? text : plain(text, names, inLink)}</Fragment>;
    }
    const children = render(n.children, names, inCode || n.t === 'code' || n.t === 'pre', inLink || n.t === 'link');
    switch (n.t) {
      case 'bold':
        return <strong key={i}>{children}</strong>;
      case 'italic':
        return <em key={i}>{children}</em>;
      case 'underline':
        return <u key={i}>{children}</u>;
      case 'strike':
        return <s key={i}>{children}</s>;
      case 'code':
        return <code key={i}>{children}</code>;
      case 'pre':
        return (
          <pre key={i} data-testid="msg-pre">
            {children}
          </pre>
        );
      case 'quote':
        return <blockquote key={i}>{children}</blockquote>;
      case 'spoiler':
        return <Spoiler key={i}>{children}</Spoiler>;
      case 'link':
        // `url` đã qua `sanitizeEntities` (chỉ http/https/mailto). noopener/noreferrer: trang đích không biết nguồn.
        return (
          <a key={i} href={n.url} target="_blank" rel="noopener noreferrer nofollow">
            {children}
          </a>
        );
    }
  });
}

/** Nội dung tin văn bản có định dạng. Không dùng innerHTML — mọi thứ dựng bằng phần tử React từ văn bản thuần. */
export function RichText({ text, entities, names }: { text: string; entities?: TextEntity[]; names: string[] }) {
  return <>{render(segmentEntities(text, entities), names, false)}</>;
}
